/**
 * celestial — a soft sun/moon body: a bright core glow behind a larger soft
 * halo, anchored at a normalized sky position. A constant world fixture (sits
 * just above the sky gradient, behind the parallax planes), so a scene reads as
 * a PLACE with a sky element rather than a bare gradient. It holds its place
 * in the VIEW (skyAnchor.ts): a moon does not scroll with the ground. Reusable: a warm low
 * sun (arena dusk), a pale moon (arctic), a swollen red giant (volcanic), etc.
 *
 * Reuses the glowBlobs normalized→world placement; pure soft-light sprites
 * (createGlowSprite), so it ports behind IRenderer like the rest of the kit.
 * mooded:false — combat heat should not pulse the sun/moon.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { placeGlowBlobs } from '../atmospherePlacement';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import type { ISprite, VfxBlendMode } from '../../../rendering/IRenderer';
import { anchorToSky } from '../skyAnchor';
import { ensureMoonTexture } from './MoonRenderer';

const buildCelestial: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'celestial') return null;
  // Reuse the glowBlobs normalized→world mapping for the sky anchor.
  const [pos] = placeGlowBlobs(
    [{ atX: layer.atX, atY: layer.atY, size: layer.size, color: layer.color, alpha: layer.coreAlpha }],
    ctx.region,
  );
  if (!pos) return null;

  const haloColor = layer.haloColor ?? layer.color;
  const haloScale = layer.haloScale ?? 2.6;
  const sprites: ISprite[] = [];

  // Determine if this celestial body is a moon (explicit or by cool palette anchor)
  const isMoon = layer.bodyType === 'moon' || (layer.bodyType == null && layer.moonPhase != null);
  const moonTextureKey = isMoon
    ? ensureMoonTexture({ size: layer.size, color: layer.color, phase: layer.moonPhase, craters: layer.craters })
    : null;

  // Halo first (large + soft), then the brighter core on top → glowing body.
  const halo = createGlowSprite({
    x: pos.x, y: pos.y, size: layer.size * haloScale, color: haloColor,
    alpha: layer.haloAlpha, blendMode: layer.blendMode, parent: ctx.layerContainer,
  });
  if (halo) sprites.push(halo);

  const coreTexture = moonTextureKey ?? layer.textureKey;
  const coreBlend: VfxBlendMode = moonTextureKey ? 'normal' : (layer.blendMode ?? 'add');
  const core = createGlowSprite({
    x: pos.x, y: pos.y, size: layer.size, color: layer.color,
    alpha: layer.coreAlpha, textureKey: coreTexture, blendMode: coreBlend,
    parent: ctx.layerContainer,
  });
  if (core) sprites.push(core);

  if (!sprites.length) return null; // textures not ready — quieter scene, never a crash
  const unsub = anchorToSky(ctx, sprites.map((sprite) => ({ sprite, atX: layer.atX, atY: layer.atY })));
  return makeContentHandle({ sprites, onDestroy: unsub });
};

registerEffect({ kind: 'celestial', build: buildCelestial, depth: Z_LAYERS.CELESTIAL, mooded: false });
